/**
 * The Work view's state, reduced from the event stream alone. Pure: the same events always
 * give the same state, so a view can rebuild from history and then follow live events.
 */
import type { AuthInfo, PermissionMode, PromptDecision, PromptSpec, Todo, ToolOutput, Usage, WorkEvent } from "@agora/agent-stream";

export interface ToolItem {
  kind: "tool";
  id: string;
  tool: string;
  title: string;
  input: Record<string, unknown>;
  parent?: string | null;
  status: "running" | "done" | "error";
  output?: ToolOutput;
  truncated?: boolean;
  seq: number;
}
export interface TextItem {
  kind: "text";
  id: string;
  text: string;
  parent?: string | null;
}
export interface PromptItem {
  kind: "prompt";
  prompt: PromptSpec;
  status: "open" | PromptDecision;
  by?: string;
  note?: string;
  answers?: Record<string, string>;
}
export interface NoteItem {
  kind: "notice" | "error";
  text: string;
  hint?: string;
}
export interface PlanItem {
  kind: "plan";
  text: string;
  status: "proposed" | "approved" | "changes";
}
export interface ChecklistItem {
  kind: "checklist";
  items: Todo[];
}
export type Item = ToolItem | TextItem | PromptItem | NoteItem | PlanItem | ChecklistItem;

/** Checklist tools show as one checklist block in the chat, not as rows. */
export const CHECKLIST_TOOLS = new Set(["TaskCreate", "TaskUpdate", "TaskList", "TaskGet", "TodoWrite"]);

export interface Turn {
  id: string;
  text: string;
  from: string;
  roomName?: string;
  at: string;
  items: Item[];
  usage?: Usage;
  finished?: { ok: boolean; result: string; subtype: string };
}

export interface WorkState {
  lastSeq: number;
  session?: { model: string; cwd: string; version: string; auth: AuthInfo; repo?: string; branch?: string; tools: string[] };
  mode: PermissionMode;
  status: { state: string; detail?: string };
  closed?: string;
  turns: Turn[];
  tools: Record<string, ToolItem>;
  prompts: Record<string, PromptItem>;
  todos: Todo[];
  plan?: { text: string; status: "proposed" | "approved" | "changes"; file?: string };
  usage: { session?: Usage; models: Record<string, { input: number; output: number; cacheRead: number; cacheWrite: number; costUsd: number }> };
  rate?: { status: string; window?: string; utilization?: number; resetsAt?: number; creditsRequired?: boolean };
  /** Live text of the reply being written; replaced by the full text. */
  typing: string;
  /** Files the session read or changed, newest first. */
  files: string[];
  /** The newest thing that needs the owner or went wrong: what makes an auto open view pop up. */
  attention: number;
}

export const emptyState = (): WorkState => ({ lastSeq: 0, mode: "default", status: { state: "idle" }, turns: [], tools: {}, prompts: {}, todos: [], usage: { models: {} }, typing: "", files: [], attention: 0 });

function current(s: WorkState, at: string): Turn {
  let t = s.turns.at(-1);
  if (!t) {
    t = { id: "start", text: "", from: "", at, items: [] };
    s.turns.push(t);
  }
  return t;
}

const pathOf = (input: Record<string, unknown>) => (typeof input.file_path === "string" ? input.file_path : typeof input.notebook_path === "string" ? input.notebook_path : "");

/** Applies one event. Returns a new state object (shallow) so React sees the change. */
export function reduce(prev: WorkState, e: WorkEvent): WorkState {
  if (e.kind !== "text.delta" && e.seq > 0 && e.seq <= prev.lastSeq) return prev;
  const s: WorkState = { ...prev, turns: prev.turns.slice(), lastSeq: e.seq > 0 ? e.seq : prev.lastSeq };
  const turn = () => {
    const t = current(s, e.at);
    const copy = { ...t, items: t.items.slice() };
    s.turns[s.turns.length - 1] = copy;
    return copy;
  };
  switch (e.kind) {
    case "session.started":
      s.session = { model: e.model, cwd: e.cwd, version: e.version, auth: e.auth, repo: e.repo, branch: e.branch, tools: e.tools };
      s.mode = e.mode;
      s.closed = undefined;
      if (!e.auth.ok) s.attention = e.seq;
      break;
    case "session.closed":
      s.closed = e.reason;
      s.status = { state: "closed", detail: e.reason };
      break;
    case "turn.started":
      s.turns.push({ id: e.turnId, text: e.text, from: e.from, roomName: e.roomName, at: e.at, items: [] });
      s.typing = "";
      break;
    case "text.delta":
      s.typing = prev.typing + e.delta;
      break;
    case "text":
      turn().items.push({ kind: "text", id: e.id, text: e.text, parent: e.parent });
      s.typing = "";
      break;
    case "thinking":
      break;
    case "tool.started": {
      const item: ToolItem = { kind: "tool", id: e.toolUseId, tool: e.tool, title: e.title, input: e.input, parent: e.parent, status: "running", seq: e.seq };
      s.tools = { ...prev.tools, [e.toolUseId]: item };
      s.typing = "";
      if (CHECKLIST_TOOLS.has(e.tool)) break;
      const t = turn();
      // A card for this call may have come first: it goes right after its tool row.
      const card = t.items.findIndex((i) => i.kind === "prompt" && i.prompt.toolUseId === e.toolUseId);
      if (card >= 0) {
        // What arrived between the card and its tool came before the tool: tool and card go after it.
        const cardItem = t.items[card]!;
        const between = t.items.slice(card + 1);
        t.items.splice(card, t.items.length - card, ...between, item, cardItem);
      } else t.items.push(item);
      const p = pathOf(e.input);
      if (p) s.files = [p, ...prev.files.filter((f) => f !== p)].slice(0, 200);
      break;
    }
    case "tool.finished": {
      const old = prev.tools[e.toolUseId];
      if (!old) break;
      const item: ToolItem = { ...old, status: e.ok ? "done" : "error", output: e.output, truncated: e.truncated };
      s.tools = { ...prev.tools, [e.toolUseId]: item };
      // The row in its turn shows the same item.
      s.turns = s.turns.map((t) => (t.items.includes(old) ? { ...t, items: t.items.map((i) => (i === old ? item : i)) } : t));
      break;
    }
    case "todos": {
      s.todos = e.items;
      // One checklist per turn, updated where it first appeared.
      const t = turn();
      const at = t.items.findIndex((i) => i.kind === "checklist");
      if (at >= 0) t.items[at] = { kind: "checklist", items: e.items };
      else t.items.push({ kind: "checklist", items: e.items });
      break;
    }
    case "plan":
      s.plan = { text: e.text || prev.plan?.text || "", status: e.status, file: e.file };
      if (e.status === "proposed") turn().items.push({ kind: "plan", text: e.text, status: e.status });
      break;
    case "prompt.opened": {
      const item: PromptItem = { kind: "prompt", prompt: e.prompt, status: "open" };
      s.prompts = { ...prev.prompts, [e.prompt.promptId]: item };
      turn().items.push(item);
      s.attention = e.seq;
      break;
    }
    case "prompt.closed": {
      const old = prev.prompts[e.promptId];
      if (!old) break;
      const item: PromptItem = { ...old, status: e.decision, by: e.by, note: e.note, answers: e.answers };
      s.prompts = { ...prev.prompts, [e.promptId]: item };
      s.turns = s.turns.map((t) => (t.items.includes(old) ? { ...t, items: t.items.map((i) => (i === old ? item : i)) } : t));
      break;
    }
    case "mode":
      s.mode = e.mode;
      break;
    case "usage": {
      s.usage = { session: e.session, models: e.models };
      const t = s.turns.at(-1);
      if (t) s.turns[s.turns.length - 1] = { ...t, usage: e.turn };
      break;
    }
    case "status":
      s.status = { state: e.state, detail: e.detail };
      break;
    case "rate_limit":
      s.rate = { status: e.status, window: e.window, utilization: e.utilization, resetsAt: e.resetsAt, creditsRequired: e.creditsRequired };
      if (e.creditsRequired || e.status === "rejected") s.attention = e.seq;
      break;
    case "notice":
      turn().items.push({ kind: "notice", text: e.text });
      break;
    case "error":
      turn().items.push({ kind: "error", text: e.message, hint: e.hint });
      s.attention = e.seq;
      break;
    case "turn.finished": {
      const t = s.turns.findIndex((x) => x.id === e.turnId);
      const i = t >= 0 ? t : s.turns.length - 1;
      if (i >= 0) s.turns[i] = { ...s.turns[i]!, finished: { ok: e.ok, result: e.result, subtype: e.subtype } };
      s.typing = "";
      if (!e.ok) s.attention = e.seq;
      break;
    }
  }
  return s;
}

export function reduceAll(events: WorkEvent[], from: WorkState = emptyState()): WorkState {
  return events.reduce(reduce, from);
}

/** Prompts still waiting, oldest first. */
export const openPrompts = (s: WorkState) => Object.values(s.prompts).filter((p) => p.status === "open");
